import { FC, ReactNode } from "react";

import { Box, Stack, SxProps, Theme } from "@mui/material";

import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox/DashboardCheckbox";
import { DashboardIconBlock } from "@/dashboard-design-system/DashboardIconBlock/DashboardIconBlock";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { IconElement } from "@/icons/types";

const questionRowSx =
  (checked: boolean): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    padding: theme.spacing(1, 2),
    borderRadius: 1,
    cursor: "pointer",
    border: "1px solid",
    borderColor: checked ? theme.palette.border.brand.intense : "transparent",
    backgroundColor: checked ? theme.palette.surface.brand.quiet : "transparent",
    transition: theme.transitions.create(["background-color", "border-color"], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeIn,
    }),
    "&:hover": {
      backgroundColor: theme.palette.surface.brand.quiet,
    },
  });

interface QuestionRowProps {
  icon?: IconElement;
  label: string;
  subtitle?: ReactNode;
  checked: boolean;
  onClick: () => void;
  "data-test"?: string;
}

const QuestionRow: FC<QuestionRowProps> = ({ icon, label, subtitle, checked, onClick, "data-test": dataTest }) => (
  <Box sx={questionRowSx(checked)} onClick={onClick} data-test={dataTest}>
    {icon && (
      <DashboardIconBlock
        vibe="neutral"
        size="medium"
        sx={{ flexShrink: 0, ...(checked && { backgroundColor: "transparent" }) }}
        slotProps={checked ? { icon: { vibe: "text-brand-moderate" } } : undefined}
      >
        {icon}
      </DashboardIconBlock>
    )}
    <Stack sx={{ minWidth: 0, flex: 1 }}>
      <DashboardTypography variant="body2" vibe="text-neutral-intense">
        {label}
      </DashboardTypography>
      {subtitle && (
        <DashboardTypography
          variant="caption"
          vibe="text-neutral-supershy"
          sx={{ fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {subtitle}
        </DashboardTypography>
      )}
    </Stack>
    <DashboardCheckbox
      checked={checked}
      size="medium"
      onChange={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  </Box>
);

export interface SelectableItem {
  id: string;
  icon?: IconElement;
  label: string;
  subtitle?: ReactNode;
  "data-test"?: string;
}

interface ExistingQuestionsListProps {
  items: SelectableItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}

export const ExistingQuestionsList: FC<ExistingQuestionsListProps> = ({ items, selectedIds, onToggle }) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <Stack spacing={0.5}>
      {items.map((item) => (
        <QuestionRow
          key={item.id}
          icon={item.icon}
          label={item.label}
          subtitle={item.subtitle}
          checked={selectedIds.has(item.id)}
          onClick={() => onToggle(item.id)}
          data-test={item["data-test"]}
        />
      ))}
    </Stack>
  );
};
